import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './AssessmentPage.css';

const Fees = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('revealed');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const els = document.querySelectorAll('.scroll-reveal');
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  return (
    <div className="page-assessment" data-testid="fees-page">
      <SEO
        title="Fees & Rebates | Clinical Minds Psychology"
        description="Fee information for psychology assessments and therapy at Clinical Minds. Medicare rebates with a Mental Health Treatment Plan, NDIS funding for assessments and reports, private fees available on enquiry."
        canonicalUrl="https://www.clinicalminds.net/fees"
      />
      <ScrollIndicator />

      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Fees</span>
          <h1 className="heading-hero">Fees & Rebates</h1>
        </div>
      </section>

      <section className="assess-section scroll-reveal">
        <div className="container assess-narrow">
          <span className="section-label">Therapy</span>
          <h2 className="heading-1">Therapy fees and Medicare rebates</h2>
          <div className="assess-body">
            <p className="body-medium">
              Individual therapy sessions are $220 for a standard 50-minute session.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              With a Mental Health Treatment Plan from your GP, you can claim a Medicare rebate of $101.55 per session, which brings your out-of-pocket cost to around $118.45. A Mental Health Treatment Plan gives you access to up to 10 rebated sessions per calendar year. The rebate is indexed by the Government each year on 1 July.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              Medicare rebates apply to therapy sessions only. There is generally no Medicare rebate for psychological assessments, though in some cases a paediatrician can refer for a complex neurodevelopmental assessment where some rebates apply, and NDIS funding may be available.
            </p>
          </div>
        </div>
      </section>

      <section className="assess-section assess-alt scroll-reveal">
        <div className="container assess-narrow">
          <span className="section-label">NDIS</span>
          <h2 className="heading-1">NDIS funding</h2>
          <div className="assess-body">
            <p className="body-medium">
              The NDIS can fund therapy and early intervention supports, and in many plans, assessments and reports. Funding depends on how your individual plan is set up, so please speak with your support coordinator or plan manager to confirm what your plan covers. We're happy to help you work through this.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              We can work with your plan manager or support coordinator to arrange services. If you're unsure whether your plan covers assessments, please get in touch and we can help you find out.
            </p>
          </div>
        </div>
      </section>

      <section className="assess-section scroll-reveal">
        <div className="container assess-narrow">
          <span className="section-label">Assessments</span>
          <h2 className="heading-1">Assessment fees</h2>
          <div className="assess-body">
            <p className="body-medium">
              Assessment fees depend on the type of assessment and its complexity. Every assessment is scoped to the individual person and question, so rather than a single fixed price we provide a quote up front. Contact us to talk through what you're looking for and we'll give you a clear quote before you commit to anything. Funding options including the NDIS are covered above.
            </p>
          </div>
        </div>
      </section>

      <section className="assess-cta-section">
        <div className="container">
          <div className="assess-cta-card">
            <h2 className="heading-2">Questions about fees?</h2>
            <p className="body-large">Please don't hesitate to get in touch.</p>
            <div className="assess-cta-buttons">
              <Link to="/contact" className="btn-primary" data-testid="fees-contact-btn">Contact Us</Link>
              <a href="tel:0266002710" className="assess-phone-cta">
                02 6600 2710
              </a>
            </div>
          </div>
        </div>
      </section>

      <CrisisSupport />
    </div>
  );
};

export default Fees;
